/**
 * Listen-for-test-event dialog (n8n-style).
 *
 * Mints a one-shot test webhook URL via ``POST /workflows/{id}/test-
 * webhook``, then long-polls the await endpoint until either:
 *   * a real HTTP request lands on the URL → resolves with the body
 *     (full-fidelity: caller decides what to do with it — usually
 *     feed it into a test-run as the trigger payload)
 *   * the user clicks Stop / closes → DELETEs the slot so the
 *     backend cleans up immediately instead of waiting for TTL
 *
 * Shared between two surfaces:
 *   * NodeEditorDrawer → Execute Node on a webhook trigger
 *   * TestRunDrawer    → canvas-level Run on a webhook-triggered DAG
 *
 * Strict-mode safe: the mint effect uses a ref guard so React's dev
 * double-render doesn't orphan a slot on the backend. A cancellation
 * flag prevents an in-flight long-poll that races a Stop from firing
 * ``onCapture`` on an already-closing dialog.
 */
import { Loader2 } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { workflowsApi, type TriggerStrategy } from "@/lib/api-workflows";


export interface WebhookListenDialogProps {
  /** The trigger strategy, used for the dialog title + the
   *  production-URL hint shown alongside the test URL. */
  strategy: TriggerStrategy;
  workflowId: string;
  /** Custom dialog title prefix. Defaults to "Execute Node" for the
   *  node modal flow; the canvas TestRunDrawer passes "Test Run". */
  titlePrefix?: string;
  /** Closes the dialog with no capture (Stop button / X). The hook
   *  fires DELETE on the slot so the backend slot is freed eagerly. */
  onCancel: () => void;
  /** Fires when a real HTTP request was captured. ``payload`` is the
   *  request body; ``token`` is the test webhook token (used by the
   *  caller to publish the test-run result back via
   *  ``workflowsApi.respondTestWebhook`` so the curl caller sees the
   *  same body a production hit would). */
  onCapture: (payload: unknown, token: string) => void;
}


export function WebhookListenDialog({
  strategy,
  workflowId,
  titlePrefix = "Execute Node",
  onCancel,
  onCapture,
}: WebhookListenDialogProps) {
  // Two phases: minting → listening.
  const [token, setToken] = React.useState<string | null>(null);
  const [capturePath, setCapturePath] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Guard against React strict-mode double-mount mutating the
  // backend twice.
  const mintedRef = React.useRef(false);
  // Cancellation flag for the poll loop.
  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    // Reset cancellation BEFORE the mint guard. React StrictMode
    // mounts → unmounts → re-mounts effects in dev; the unmount
    // cleanup flips ``cancelledRef`` to true. If we bail out on the
    // re-mount before resetting it, the still-in-flight mint from
    // the FIRST mount resolves, sees the stale cancel flag, and
    // immediately DELETEs the slot it just minted — UI hangs on
    // "Allocating…" forever. Resetting here makes the re-mount a
    // proper continuation of the same listener instance.
    cancelledRef.current = false;
    if (mintedRef.current) return;
    mintedRef.current = true;
    let currentToken: string | null = null;

    (async () => {
      try {
        const minted = await workflowsApi.mintTestWebhook(workflowId);
        if (cancelledRef.current) {
          // Closed while mint was in flight — free the slot.
          workflowsApi
            .cancelTestWebhook(workflowId, minted.token)
            .catch(() => undefined);
          return;
        }
        currentToken = minted.token;
        setToken(minted.token);
        setCapturePath(minted.capture_path);

        // Poll loop: 30s per round. 408 = keep waiting; anything
        // else stops the loop and surfaces.
        while (!cancelledRef.current) {
          try {
            const captured = await workflowsApi.awaitTestWebhook(
              workflowId,
              minted.token,
              30,
            );
            if (cancelledRef.current) return;
            onCapture(captured.body, minted.token);
            return;
          } catch (e: unknown) {
            const status = (e as { status?: number } | undefined)?.status;
            if (status === 408) continue;
            setError(
              e instanceof Error
                ? e.message
                : "Listener failed (token expired or backend error)",
            );
            return;
          }
        }
      } catch (e: unknown) {
        setError(
          e instanceof Error ? e.message : "Failed to start listener",
        );
      }
    })();

    return () => {
      cancelledRef.current = true;
      if (currentToken) {
        workflowsApi
          .cancelTestWebhook(workflowId, currentToken)
          .catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const resolvedUrl =
    capturePath && typeof window !== "undefined"
      ? `${window.location.protocol}//${window.location.host}${capturePath}`
      : capturePath;
  const realUrl = strategy.external_url
    ? strategy.external_url.replace(
        "<host>",
        typeof window !== "undefined"
          ? `${window.location.protocol}//${window.location.host}`
          : "<host>",
      )
    : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:!max-w-xl p-0 flex flex-col">
        <div className="border-b border-border/60 px-5 py-3">
          <DialogTitle className="text-sm font-semibold">
            {titlePrefix} — {strategy.label}
          </DialogTitle>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Listening for a real HTTP request. The first hit becomes
            this run's initial payload.
          </p>
        </div>

        <div className="space-y-3 px-5 py-4">
          {error ? (
            <Alert tone="danger">
              <p className="text-xs">{error}</p>
            </Alert>
          ) : !token ? (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
              Allocating test webhook URL…
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[11px] text-info">
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
                Waiting for HTTP request…
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Test URL (any HTTP method)
                </div>
                <code className="block break-all rounded bg-muted/40 px-2 py-1 font-mono text-[11px]">
                  {resolvedUrl}
                </code>
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  curl example
                </div>
                <pre className="overflow-x-auto rounded bg-muted/40 px-2 py-1 font-mono text-[10px]">
                  {`curl -X POST '${resolvedUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '{"sample": "payload"}'`}
                </pre>
              </div>
              {realUrl && (
                <p className="text-[10px] text-muted-foreground">
                  Production webhook URL ({realUrl}) is unaffected — this
                  is a one-shot test endpoint that captures the next
                  request and discards itself.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-5 py-3">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            {error ? "Close" : "Stop"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
